import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-32 bg-zinc-950">
      <div className="max-w-4xl mx-auto px-8">
        <div
          data-animate
          className="inline-block text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-5 transition-all duration-700"
          style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif" }}
        >
          About Us
        </div>
        <h2
          data-animate
          className="text-white font-bold mb-6 leading-tight transition-all duration-700"
          style={{
            opacity: 0,
            transform: 'translateY(20px)',
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            transitionDelay: '120ms',
          }}
        >
          Crafting Secure &amp; Elegant iOS Experiences
        </h2>
        <p
          data-animate
          className="text-zinc-400 leading-relaxed mb-6 transition-all duration-700"
          style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif", fontSize: '1.0625rem', transitionDelay: '240ms' }}
        >
          Founded in Plovdiv, Bulgaria, <strong className="text-white">BORVIL OOD</strong> is driven by a singular mission — to make every iPhone and iPad
          faster, cleaner, and more secure. Our engineers combine deep knowledge of the iOS platform
          with a relentless focus on performance and privacy.
        </p>
        <p
          data-animate
          className="text-zinc-400 leading-relaxed mb-12 transition-all duration-700"
          style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif", fontSize: '1.0625rem', transitionDelay: '360ms' }}
        >
          Every product we ship is built with precision — no bloat, no shortcuts. We believe technology
          should be invisible, empowering users without getting in their way.
        </p>

        <div className="flex flex-col gap-5">
          <div
            data-animate
            className="flex items-center gap-4 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', transitionDelay: '480ms' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30 flex-shrink-0">
              <i className="ri-shield-check-line text-emerald-400 text-lg"></i>
            </div>
            <span className="text-zinc-300 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Privacy-first philosophy in every product</span>
          </div>
          <div
            data-animate
            className="flex items-center gap-4 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', transitionDelay: '560ms' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30 flex-shrink-0">
              <i className="ri-apple-line text-emerald-400 text-lg"></i>
            </div>
            <span className="text-zinc-300 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Native iOS engineering at its finest</span>
          </div>
          <div
            data-animate
            className="flex items-center gap-4 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', transitionDelay: '640ms' }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/10 border border-emerald-500/30 flex-shrink-0">
              <i className="ri-global-line text-emerald-400 text-lg"></i>
            </div>
            <span className="text-zinc-300 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Trusted by users across the globe</span>
          </div>
        </div>
      </div>
    </section>
  );
}
